import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Arrows, StepInfo } from '../ModalConfigurationSteps.types';
import { FlatList, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { styleModalDesktop, styleModalMobile } from '../ModalConfigurationSteps.style';
import { CARDS_COMPONENTS } from './StepCard.constants';
import { CardsProps } from './StepCard.types';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';

export const useStepCardLogic = <T extends StepInfo>({
  cards,
  onSelectCard,
  onSelectCardMobile,
  getIsSuggestedCard,
  configurationStepId,
}: CardsProps<T>) => {
  // Add logic here if needed in the future
  const { isDesktop } = useResponsiveScreen();

  const initialScrollIndex = useMemo(() => {
    const suggestedIndex = cards.findIndex(getIsSuggestedCard);
    return suggestedIndex && suggestedIndex >= 1 ? suggestedIndex - 1 : 0;
  }, []);

  const [selectedCard, setSelectedCard] = useState<T>(() => {
    const suggestedCard = cards.find(getIsSuggestedCard);
    return suggestedCard ?? cards[0];
  });
  const scrollViewRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState<number>(initialScrollIndex);
  const [arrows, setArrows] = useState<Arrows>(cards.length > 4 ? Arrows.SHOW_RIGHT : Arrows.HIDDEN);

  const styleModal = isDesktop ? styleModalDesktop : styleModalMobile;

  const Card = useMemo(() => {
    return CARDS_COMPONENTS[configurationStepId];
  }, [configurationStepId]);

  const scrollToIndex = (index: number, animated = true) => {
    setActiveIndex(prev => {
      if (index >= 0 && index < cards.length) {
        if (cards.length <= 4) {
          setArrows(Arrows.HIDDEN);
        } else if (index > 0 && index < cards.length - 4) {
          setArrows(Arrows.SHOW_BOTH);
        } else if (index == 0) {
          setArrows(Arrows.SHOW_RIGHT);
        } else if (index >= cards.length - 4) {
          setArrows(Arrows.SHOW_LEFT);
        }
        scrollViewRef.current?.scrollToIndex({ index, animated });
        return index;
      } else {
        scrollViewRef.current?.scrollToIndex({ index: prev, animated });
        return prev;
      }
    });
  };

  const onScrollHandler = useDebounceCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isDesktop) {
      const offsetX = e.nativeEvent.contentOffset.x;
      const index = Math.round(offsetX / 210);
      scrollToIndex(index, true);
    }
  }, 100);

  useEffect(() => {
    isDesktop && onPressCard(selectedCard);
  }, []);

  const onPressCard = (card: T) => {
    if (isDesktop) {
      setSelectedCard(card);
      onSelectCard(card.productConfigurationId);
    } else {
      onSelectCardMobile(card.productConfigurationId);
    }
    amplitude.trackEvent(AnalyticsEvents.lookup.select, {
      lookupcode: configurationStepId,
      lookupId: card.code,
    });
  };

  const onFocusCard = (card: T, event: any) => {
    if (getIsSuggestedCard(card)) {
      event.stopPropagation();
      const index = cards.findIndex(currentCard => card.code === currentCard.code);
      scrollToIndex(index - 1, false);
    }
  };

  return {
    scrollViewRef,
    initialScrollIndex,
    activeIndex,
    scrollToIndex,
    arrows,
    selectedCard,
    onPressCard,
    onFocusCard,
    styleModal,
    Card,
    onScrollHandler,
  };
};
